/**
 * Print cover wrap geometry for KDP + IngramSpark paperbacks.
 *
 * Specs verified Jul 2026 against:
 * - KDP Help “Create a Paperback Cover” (G201953020):
 *   white 0.002252"/page, cream 0.0025"/page, premium color 0.002347"/page,
 *   standard color 0.002252"/page; bleed 0.125"; spine text min 79 pages;
 *   spine text inset 0.0625" from spine edges.
 * - IngramSpark File Creation Guide: bleed 0.125" all outer edges;
 *   no spine text below 48 pages; spine safety 0.0625" (≥0.35" spine)
 *   or 0.03125" (<0.35" spine).
 * - Barcode clear zone (KDP auto-barcode): 2" × 1.2" on back, ~0.25" from
 *   bottom trim and ~0.375" from right (outer) trim — UI overlay only.
 */

import type { TrimSize } from "./publishFormattingShared";
import { TRIM_SIZE_PT } from "./publishFormattingShared";

export type CoverPlatform = "kdp-print" | "ingramspark";
export type PaperStock = "white" | "cream" | "premium-color" | "standard-color";

/** Inches per page (caliper) — KDP published factors; IngramSpark is within rounding. */
export const PAPER_CALIPER_IN: Record<PaperStock, number> = {
  white: 0.002252,
  cream: 0.0025,
  "premium-color": 0.002347,
  "standard-color": 0.002252,
};

export const COVER_BLEED_IN = 0.125;
export const COVER_SAFE_MARGIN_IN = 0.5;
export const SPINE_TEXT_INSET_IN = 0.0625;
export const KDP_SPINE_TEXT_MIN_PAGES = 79;
export const INGRAM_SPINE_TEXT_MIN_PAGES = 48;

/** KDP auto-barcode clear zone on the back cover (inches). */
export const BARCODE_ZONE = {
  widthIn: 2,
  heightIn: 1.2,
  /** From back-cover trim bottom edge. */
  offsetBottomIn: 0.25,
  /** From back-cover trim outer (right when LTR wrap) edge. */
  offsetOuterIn: 0.375,
} as const;

export type CoverGeometry = {
  platform: CoverPlatform;
  paperStock: PaperStock;
  trimSize: TrimSize;
  pageCount: number;
  /** Trim width/height in inches. */
  trimWIn: number;
  trimHIn: number;
  /** Spine width in inches (full precision — do not round for export). */
  spineWIn: number;
  bleedIn: number;
  /** Full wrap including bleed. */
  wrapWIn: number;
  wrapHIn: number;
  /** Same in points (72 pt/in). */
  trimWPt: number;
  trimHPt: number;
  spineWPt: number;
  bleedPt: number;
  wrapWPt: number;
  wrapHPt: number;
  /** Whether spine text is allowed. */
  allowSpineText: boolean;
  spineTextMinPages: number;
  /** Spine safety inset (Ingram varies by spine width). */
  spineSafetyIn: number;
  /** Rects in wrap coordinates (origin top-left of full wrap including bleed), inches. */
  regions: {
    bleed: { x: number; y: number; w: number; h: number };
    /** Back panel trim box */
    backTrim: { x: number; y: number; w: number; h: number };
    spine: { x: number; y: number; w: number; h: number };
    frontTrim: { x: number; y: number; w: number; h: number };
    backSafe: { x: number; y: number; w: number; h: number };
    frontSafe: { x: number; y: number; w: number; h: number };
    spineSafe: { x: number; y: number; w: number; h: number };
    barcode: { x: number; y: number; w: number; h: number };
  };
};

export type BookCoverLayout = {
  version: 1;
  platform: CoverPlatform;
  paperStock: PaperStock;
  trimSize: TrimSize;
  pageCount: number;
  /** Image placement relative to full wrap (normalized 0–1 of wrap size) or absolute inches. */
  image: {
    /** data URL or file path / cloud URL */
    src: string | null;
    /** Pan offset in inches from wrap origin (image top-left). */
    offsetXIn: number;
    offsetYIn: number;
    /** Scale relative to “fit cover” (1 = cover the wrap). */
    scale: number;
  };
  spineTitle: string;
  spineAuthor: string;
  updatedAt: string;
};

export function defaultCoverLayout(partial?: Partial<BookCoverLayout>): BookCoverLayout {
  const baseImage = {
    src: null as string | null,
    offsetXIn: 0,
    offsetYIn: 0,
    scale: 1,
  };
  return {
    version: 1,
    platform: "kdp-print",
    paperStock: "cream",
    trimSize: "6x9",
    pageCount: 200,
    spineTitle: "",
    spineAuthor: "",
    updatedAt: new Date().toISOString(),
    ...partial,
    image: {
      ...baseImage,
      ...(partial?.image ?? {}),
    },
  };
}

export function trimSizeInches(trimSize: TrimSize): { w: number; h: number } {
  const pt = TRIM_SIZE_PT[trimSize];
  return { w: pt.w / 72, h: pt.h / 72 };
}

export function spineWidthInches(pageCount: number, paperStock: PaperStock): number {
  const pages = Math.max(0, Math.floor(pageCount));
  return pages * PAPER_CALIPER_IN[paperStock];
}

export function spineTextAllowed(
  platform: CoverPlatform,
  pageCount: number
): { allowed: boolean; minPages: number } {
  const minPages =
    platform === "ingramspark" ? INGRAM_SPINE_TEXT_MIN_PAGES : KDP_SPINE_TEXT_MIN_PAGES;
  return { allowed: pageCount >= minPages, minPages };
}

export function spineSafetyInches(platform: CoverPlatform, spineWIn: number): number {
  if (platform === "ingramspark") {
    return spineWIn >= 0.35 ? 0.0625 : 0.03125;
  }
  return SPINE_TEXT_INSET_IN;
}

/**
 * Compute full-wrap cover geometry.
 * Layout LTR: [bleed|back|spine|front|bleed] horizontally, bleed top/bottom.
 */
export function computeCoverGeometry(input: {
  platform: CoverPlatform;
  paperStock: PaperStock;
  trimSize: TrimSize;
  pageCount: number;
}): CoverGeometry {
  const { platform, paperStock, trimSize } = input;
  const pageCount = Math.max(0, Math.floor(input.pageCount));
  const { w: trimWIn, h: trimHIn } = trimSizeInches(trimSize);
  const spineWIn = spineWidthInches(pageCount, paperStock);
  const bleedIn = COVER_BLEED_IN;
  const wrapWIn = trimWIn * 2 + spineWIn + bleedIn * 2;
  const wrapHIn = trimHIn + bleedIn * 2;

  const { allowed, minPages } = spineTextAllowed(platform, pageCount);
  const spineSafetyIn = spineSafetyInches(platform, spineWIn);

  const backX = bleedIn;
  const spineX = bleedIn + trimWIn;
  const frontX = bleedIn + trimWIn + spineWIn;
  const trimY = bleedIn;

  const backSafe = {
    x: backX + COVER_SAFE_MARGIN_IN,
    y: trimY + COVER_SAFE_MARGIN_IN,
    w: Math.max(0, trimWIn - COVER_SAFE_MARGIN_IN * 2),
    h: Math.max(0, trimHIn - COVER_SAFE_MARGIN_IN * 2),
  };

  const frontSafe = {
    x: frontX + COVER_SAFE_MARGIN_IN,
    y: trimY + COVER_SAFE_MARGIN_IN,
    w: Math.max(0, trimWIn - COVER_SAFE_MARGIN_IN * 2),
    h: Math.max(0, trimHIn - COVER_SAFE_MARGIN_IN * 2),
  };

  const spineSafe = {
    x: spineX + spineSafetyIn,
    y: trimY + spineSafetyIn,
    w: Math.max(0, spineWIn - spineSafetyIn * 2),
    h: Math.max(0, trimHIn - spineSafetyIn * 2),
  };

  // Barcode on back: from back trim's bottom-right (outer edge of back = right of back panel)
  const barcode = {
    x: backX + trimWIn - BARCODE_ZONE.offsetOuterIn - BARCODE_ZONE.widthIn,
    y: trimY + trimHIn - BARCODE_ZONE.offsetBottomIn - BARCODE_ZONE.heightIn,
    w: BARCODE_ZONE.widthIn,
    h: BARCODE_ZONE.heightIn,
  };

  return {
    platform,
    paperStock,
    trimSize,
    pageCount,
    trimWIn,
    trimHIn,
    spineWIn,
    bleedIn,
    wrapWIn,
    wrapHIn,
    trimWPt: trimWIn * 72,
    trimHPt: trimHIn * 72,
    spineWPt: spineWIn * 72,
    bleedPt: bleedIn * 72,
    wrapWPt: wrapWIn * 72,
    wrapHPt: wrapHIn * 72,
    allowSpineText: allowed,
    spineTextMinPages: minPages,
    spineSafetyIn,
    regions: {
      bleed: { x: 0, y: 0, w: wrapWIn, h: wrapHIn },
      backTrim: { x: backX, y: trimY, w: trimWIn, h: trimHIn },
      spine: { x: spineX, y: trimY, w: spineWIn, h: trimHIn },
      frontTrim: { x: frontX, y: trimY, w: trimWIn, h: trimHIn },
      backSafe,
      frontSafe,
      spineSafe,
      barcode,
    },
  };
}

/** 300 DPI pixel size of the full wrap. */
export function wrapPixelsAtDpi(geometry: CoverGeometry, dpi = 300): { w: number; h: number } {
  return {
    w: Math.round(geometry.wrapWIn * dpi),
    h: Math.round(geometry.wrapHIn * dpi),
  };
}

/** Estimate effective DPI of an image covering the wrap at the given scale. */
export function estimateCoverImageDpi(params: {
  imageWidthPx: number;
  imageHeightPx: number;
  geometry: CoverGeometry;
  scale: number;
}): number {
  const { imageWidthPx, imageHeightPx, geometry, scale } = params;
  // At scale=1, image is fitted to cover the wrap (object-fit: cover).
  const wrapAspect = geometry.wrapWIn / geometry.wrapHIn;
  const imgAspect = imageWidthPx / Math.max(1, imageHeightPx);
  let drawnWIn: number;
  let drawnHIn: number;
  if (imgAspect > wrapAspect) {
    drawnHIn = geometry.wrapHIn * scale;
    drawnWIn = drawnHIn * imgAspect;
  } else {
    drawnWIn = geometry.wrapWIn * scale;
    drawnHIn = drawnWIn / imgAspect;
  }
  const dpiX = imageWidthPx / Math.max(0.001, drawnWIn);
  const dpiY = imageHeightPx / Math.max(0.001, drawnHIn);
  return Math.min(dpiX, dpiY);
}

export function parseCoverLayout(raw: unknown): BookCoverLayout {
  const base = defaultCoverLayout();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Record<string, unknown>;
  const img = o.image && typeof o.image === "object" ? (o.image as Record<string, unknown>) : {};
  return defaultCoverLayout({
    platform: o.platform === "ingramspark" ? "ingramspark" : "kdp-print",
    paperStock:
      o.paperStock === "white" ||
      o.paperStock === "cream" ||
      o.paperStock === "premium-color" ||
      o.paperStock === "standard-color"
        ? o.paperStock
        : base.paperStock,
    trimSize: (typeof o.trimSize === "string" ? o.trimSize : base.trimSize) as TrimSize,
    pageCount: typeof o.pageCount === "number" ? Math.max(0, Math.floor(o.pageCount)) : base.pageCount,
    image: {
      src: typeof img.src === "string" ? img.src : null,
      offsetXIn: typeof img.offsetXIn === "number" ? img.offsetXIn : 0,
      offsetYIn: typeof img.offsetYIn === "number" ? img.offsetYIn : 0,
      scale: typeof img.scale === "number" && img.scale > 0 ? img.scale : 1,
    },
    spineTitle: typeof o.spineTitle === "string" ? o.spineTitle : "",
    spineAuthor: typeof o.spineAuthor === "string" ? o.spineAuthor : "",
    updatedAt: typeof o.updatedAt === "string" ? o.updatedAt : new Date().toISOString(),
  });
}
